/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN TOURNAMENT API
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE CONFIG
============================================================= */
const BASE = "/tournament";

const ENDPOINTS = {
  PARTICIPANTS:        `${BASE}/tournament_participants.php`,
  TOURNAMENTS:         `${BASE}/tournaments.php`,
  EXPORTPARTICIPANTS:  `${BASE}/export-participants.php`,
  RECEIPT:             `${BASE}/receipt/download-receipt.php`,
};

/* =============================================================
   RESPONSE HANDLER
============================================================= */
const handleResponse = (res, fallbackMessage) => {
  if (!res || (res.status !== "success" && res.status !== true)) {
    return {
      success: false,
      message: res?.message || fallbackMessage,
      data: [],
      meta: {},
    };
  }
  return {
    success: true,
    message: res.message || "Success",
    data: Array.isArray(res.data) ? res.data : res.data ? [res.data] : [],
    meta: res.meta || {},
  };
};

/* =============================================================
   ERROR HANDLER
============================================================= */
const handleError = (error, label) => {
  console.error(`❌ ${label} Error:`, error);
  return {
    success: false,
    message: error?.message || "Unexpected error occurred",
    data: [],
    meta: {},
  };
};

/* =============================================================
   GET TOURNAMENT LIST  ← category_id added here
============================================================= */
export const getTournamentList = async (
  {
    page = 1,
    limit = 10,
    search = "",
    category_id = "",   // ← accept it
  } = {},
  options = {}
) => {
  try {
    // Build params — only include category_id when it has a value
    const params = {
      page,
      limit,
      ...(search      && { search }),
      ...(category_id && { category_id }),  // ← appended to URL only when set
    };

    const res = await apiCall(
      "GET",
      ENDPOINTS.TOURNAMENTS,
      null,
      "Fetch Tournament List",
      { params, ...options }
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to fetch tournaments",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {},
      message: res.message || "",
    };
  } catch (error) {
    return handleError(error, "Fetch Tournament List");
  }
};

/* =============================================================
   CREATE TOURNAMENT
============================================================= */
export const createTournament = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ENDPOINTS.TOURNAMENTS,
      payload,
      "Create Tournament",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to create tournament",
      };
    }

    return {
      success: true,
      message: res.message || "Tournament Created Successfully",
      data: res.data || null,
      id: res.id || null,
    };
  } catch (error) {
    return handleError(error, "Create Tournament");
  }
};

/* =============================================================
   UPDATE TOURNAMENT
============================================================= */
export const updateTournament = async (id, payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      `${ENDPOINTS.TOURNAMENTS}?id=${id}`,
      payload,
      "Update Tournament",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to update tournament",
      };
    }

    return {
      success: true,
      message: res.message || "Tournament Updated Successfully",
      data: res.data || null,
    };
  } catch (error) {
    return handleError(error, "Update Tournament");
  }
};

/* =============================================================
   DELETE TOURNAMENT
============================================================= */
export const deleteTournament = async (id, options = {}) => {
  try {
    const res = await apiCall(
      "DELETE",
      `${ENDPOINTS.TOURNAMENTS}?id=${id}`,
      null,
      "Delete Tournament",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to delete tournament",
      };
    }

    return {
      success: true,
      message: res.message || "Tournament Deleted Successfully",
    };
  } catch (error) {
    return handleError(error, "Delete Tournament");
  }
};

/* =============================================================
   GET SINGLE TOURNAMENT
============================================================= */
export const getTournamentById = async (id, options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      `${ENDPOINTS.TOURNAMENTS}?id=${id}`,
      null,
      "Fetch Tournament",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Tournament not found",
      };
    }

    return { success: true, data: res.data };
  } catch (error) {
    return handleError(error, "Fetch Tournament");
  }
};

/* =============================================================
   GET TOURNAMENTS (DROPDOWN)
============================================================= */
export const getTournaments = async (options = {}) => {
  try {
    const res = await apiCall(
      "GET",
      ENDPOINTS.TOURNAMENTS,
      null,
      "Fetch Tournaments",
      options
    );

    if (!res || (res.status !== "success" && res.status !== true)) {
      return {
        success: false,
        message: res?.message || "Failed to fetch tournaments",
        data: [],
      };
    }

    return {
      success: true,
      message: res.message || "Success",
      data: res.data || [],
    };
  } catch (error) {
    return handleError(error, "Fetch Tournaments");
  }
};

/* =============================================================
   GET PARTICIPANTS
============================================================= */
export const getParticipants = async (
  {
    tournament_id,
    page = 1,
    limit = 10,
    category = "",
    gender = "",
    search = "",
  } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ENDPOINTS.PARTICIPANTS,
      null,
      "Fetch Participants",
      {
        params: {
          tournament_id,
          page,
          limit,
          ...(category && { category }),
          ...(gender   && { gender }),
          ...(search   && { search }),
        },
        ...options,
      }
    );

    return handleResponse(res, "Failed to fetch participants");
  } catch (error) {
    return handleError(error, "Fetch Participants");
  }
};

/* =============================================================
   ADD PARTICIPANTS
============================================================= */
export const addParticipants = async (payload, options = {}) => {
  try {
    const res = await apiCall(
      "POST",
      ENDPOINTS.PARTICIPANTS,
      { action: "ADD", ...payload },
      "Add Participants",
      options
    );

    return handleResponse(res, "Failed to add participants");
  } catch (error) {
    return handleError(error, "Add Participants");
  }
};

/* =============================================================
   UPDATE PARTICIPANT
============================================================= */
export const updateParticipant = async (payload, options = {}) => {
  try {
    if (!payload?.id) {
      return { success: false, message: "Participant ID is required", data: [], meta: {} };
    }

    const res = await apiCall(
      "POST",
      ENDPOINTS.PARTICIPANTS,
      { action: "UPDATE", ...payload },
      "Update Participant",
      options
    );

    return handleResponse(res, "Failed to update participant");
  } catch (error) {
    return handleError(error, "Update Participant");
  }
};

/* =============================================================
   DELETE PARTICIPANT
============================================================= */
export const deleteParticipant = async (id, options = {}) => {
  try {
    if (!id) {
      return { success: false, message: "Participant ID is required", data: [], meta: {} };
    }

    const res = await apiCall(
      "POST",
      ENDPOINTS.PARTICIPANTS,
      { action: "DELETE", id },
      "Delete Participant",
      options
    );

    return handleResponse(res, "Failed to delete participant");
  } catch (error) {
    return handleError(error, "Delete Participant");
  }
};

/* =============================================================
   EXPORT PARTICIPANTS
============================================================= */
export const exportParticipants = async ({ tournament_id } = {}, options = {}) => {
  try {
    if (!tournament_id) {
      return { success: false, message: "Tournament ID is required", data: null };
    }

    const blob = await apiCall(
      "GET",
      ENDPOINTS.EXPORTPARTICIPANTS,
      null,
      "Export Participants",
      { params: { tournament_id }, responseType: "blob", ...options }
    );

    if (!blob || blob.size === 0) throw new Error("Empty file received");

    return { success: true, message: "Export successful", data: blob };
  } catch (error) {
    return handleError(error, "Export Participants");
  }
};

/* =============================================================
   DOWNLOAD RECEIPT
============================================================= */
export const downloadReceipt = async ({ participant_id } = {}, options = {}) => {
  try {
    if (!participant_id) {
      return { success: false, message: "Participant ID is required", data: null };
    }

    const blob = await apiCall(
      "GET",
      ENDPOINTS.RECEIPT,
      null,
      "Download Receipt",
      { params: { participant_id }, responseType: "blob", ...options }
    );

    if (!blob || blob.size === 0) throw new Error("Empty receipt received");

    return { success: true, message: "Receipt downloaded", data: blob };
  } catch (error) {
    return handleError(error, "Download Receipt");
  }
};